import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ShieldCheck, UserCog, UserPlus, UserCheck, Clock, Flag } from "lucide-react";
import Avatar from "../../../components/ui/Avatar";
import { fetchPublicProfileByUserId } from "../../../api/profileService";
import { sendFriendRequest, respondFriendRequest } from "../../../api/premiumSocialService";
import { useAuth } from "../../../context/AuthContext";

function getPlayerId(player) {
  const rawId = player?.uid || player?.player_id || player?.user_id || player?.id;
  return /^\d+$/.test(String(rawId || "")) ? String(rawId) : null;
}

function BadgeIcon({ badge }) {
  if (badge.icon_image) {
    return (
      <img
        src={badge.icon_image}
        alt=""
        className="h-6 w-6 rounded-lg object-cover"
      />
    );
  }

  return <span className="text-sm">{badge.icon || "*"}</span>;
}

function StatusIcons({ profile }) {
  const icons = [];

  if (profile?.is_moderator) {
    icons.push(
      <span
        key="moderator"
        title="Moderateur"
        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sky-100 text-sky-700"
      >
        <ShieldCheck className="h-3.5 w-3.5" />
      </span>
    );
  }

  if (profile?.is_admin) {
    icons.push(
      <span
        key="admin"
        title="Administrateur"
        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-rose-100 text-rose-700"
      >
        <UserCog className="h-3.5 w-3.5" />
      </span>
    );
  }

  return icons.length ? (
    <div className="mt-2 flex flex-wrap gap-1.5">{icons}</div>
  ) : null;
}

export default function PlayerProfilePreview({
  player,
  avatar,
  subtitle = null,
  isCurrentPlayer = false,
  nameClassName = "",
  subtitleClassName = "",
}) {
  const triggerRef = useRef(null);
  const { isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cardPosition, setCardPosition] = useState({ left: 0, top: 0 });
  const [friendshipStatus, setFriendshipStatus] = useState(null);
  const [friendshipDirection, setFriendshipDirection] = useState(null);
  const [friendshipId, setFriendshipId] = useState(null);
  const [friendLoading, setFriendLoading] = useState(false);
  const playerId = getPlayerId(player);
  const canOpenProfile = Boolean(playerId);
  const fallbackName = player?.pseudo || player?.username || "Joueur";

  const loadProfile = () => {
    if (!playerId || profile || isLoading) {
      return;
    }

    setIsLoading(true);
    fetchPublicProfileByUserId(playerId)
      .then((data) => {
        setProfile(data);
        setFriendshipStatus(data.friendship_status || null);
        setFriendshipDirection(data.friendship_direction || null);
        setFriendshipId(data.friendship_id || null);
      })
      .catch(() => setProfile(null))
      .finally(() => setIsLoading(false));
  };

  const handleFriendAction = async (action) => {
    if (friendLoading) return;
    setFriendLoading(true);
    try {
      if (action === "add") {
        const data = await sendFriendRequest(playerId);
        const fs = data.friendship;
        setFriendshipStatus(fs.status);
        setFriendshipDirection(fs.direction);
        setFriendshipId(fs.id);
      } else if (friendshipId) {
        const data = await respondFriendRequest(friendshipId, action);
        if (data.deleted) {
          setFriendshipStatus(null);
          setFriendshipDirection(null);
          setFriendshipId(null);
        } else {
          const fs = data.friendship;
          setFriendshipStatus(fs.status);
          setFriendshipDirection(fs.direction);
          setFriendshipId(fs.id);
        }
      }
    } catch (_err) {
    } finally {
      setFriendLoading(false);
    }
  };

  const showCard = () => {
    setIsHovering(true);
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setCardPosition({
        left: Math.max(12, Math.min(rect.left, window.innerWidth - 304)),
        top: Math.max(12, Math.min(rect.bottom + 8, window.innerHeight - 280)),
      });
    }
    loadProfile();
  };

  const openProfile = () => {
    if (!playerId) {
      return;
    }
    window.open(`/joueur/u/${playerId}`, "_blank", "noreferrer");
  };

  const openReportPage = () => {
    if (!playerId) return;
    window.open(`/signaler/joueur/${playerId}`, "_blank", "noreferrer");
  };

  const cardProfile = profile || {
    username: fallbackName,
    avatar: avatar?.image || avatar?.src || null,
    badges: [],
    best_score: player?.best_score || player?.score || 0,
    total_points: player?.total_points || player?.total_score || player?.score || 0,
    games_played: player?.games_played || 0,
    is_premium: Boolean(player?.is_premium),
    is_moderator: Boolean(player?.is_moderator),
    is_admin: Boolean(player?.is_admin || player?.is_staff || player?.is_superuser),
  };

  const hoverCard = isHovering ? (
    <div
      className="fixed z-[120] w-72 rounded-3xl border border-slate-200 bg-white p-4 text-slate-900 shadow-2xl"
      style={{ left: cardPosition.left, top: cardPosition.top }}
    >
      {isLoading && !profile ? (
        <div className="text-sm text-slate-500">Chargement du profil...</div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Avatar
              src={cardProfile.avatar || avatar?.image || avatar?.src || null}
              name={cardProfile.username}
              size="lg"
              rounded="full"
            />
            <div className="min-w-0">
              <div className="truncate font-black text-slate-950">
                {cardProfile.username}
              </div>
              <div className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-600">
                {canOpenProfile ? "Profil joueur" : "Joueur invite"}
              </div>
              <StatusIcons profile={cardProfile} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-2xl bg-slate-50 px-3 py-2 text-center">
              <div className="text-[10px] uppercase text-slate-400">Score global</div>
              <div className="font-black">{cardProfile.total_points || 0}</div>
            </div>
            <div className="rounded-2xl bg-slate-50 px-3 py-2 text-center">
              <div className="text-[10px] uppercase text-slate-400">Meilleur score</div>
              <div className="font-black">{cardProfile.best_score || 0}</div>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
              Badges
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {cardProfile.badges?.length ? (
                cardProfile.badges.slice(0, 6).map((badge) => (
                  <div
                    key={badge.id}
                    title={badge.name}
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-slate-50"
                  >
                    <BadgeIcon badge={badge} />
                  </div>
                ))
              ) : (
                <span className="text-xs text-slate-500">
                  Aucun badge public
                </span>
              )}
            </div>
          </div>

          {isCurrentPlayer ? (
            <div className="rounded-2xl bg-cyan-50 px-3 py-2 text-xs font-semibold text-cyan-700">
              C'est ton profil public.
            </div>
          ) : null}

          {!isCurrentPlayer && canOpenProfile && isAuthenticated ? (
            <div>
              {friendshipStatus === null && (
                <button
                  type="button"
                  disabled={friendLoading}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => { e.stopPropagation(); handleFriendAction("add"); }}
                  className="w-full inline-flex items-center justify-center gap-1.5 rounded-2xl bg-sky-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-60"
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  Ajouter en ami
                </button>
              )}
              {friendshipStatus === "pending" && friendshipDirection === "sent" && (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-500">
                    <Clock className="h-3 w-3" />
                    Demande envoyee
                  </span>
                  <button
                    type="button"
                    disabled={friendLoading}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => { e.stopPropagation(); handleFriendAction("cancel"); }}
                    className="rounded-xl border border-slate-200 px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-100 disabled:opacity-60"
                  >
                    Annuler
                  </button>
                </div>
              )}
              {friendshipStatus === "pending" && friendshipDirection === "received" && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={friendLoading}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => { e.stopPropagation(); handleFriendAction("accept"); }}
                    className="flex-1 rounded-2xl bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60"
                  >
                    Accepter
                  </button>
                  <button
                    type="button"
                    disabled={friendLoading}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => { e.stopPropagation(); handleFriendAction("decline"); }}
                    className="flex-1 rounded-2xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 disabled:opacity-60"
                  >
                    Refuser
                  </button>
                </div>
              )}
              {friendshipStatus === "accepted" && (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                    <UserCheck className="h-3.5 w-3.5" />
                    Ami
                  </span>
                  <button
                    type="button"
                    disabled={friendLoading}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => { e.stopPropagation(); handleFriendAction("remove"); }}
                    className="rounded-xl border border-slate-200 px-2 py-1.5 text-xs text-slate-500 hover:bg-slate-100 disabled:opacity-60"
                  >
                    Retirer
                  </button>
                </div>
              )}
            </div>
          ) : null}

          {!isCurrentPlayer && canOpenProfile ? (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => { e.stopPropagation(); openReportPage(); }}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold text-rose-700 hover:bg-rose-100"
            >
              <Flag className="h-3.5 w-3.5" />
              Signaler ce joueur
            </button>
          ) : null}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div
      ref={triggerRef}
      className="relative min-w-0"
      onMouseEnter={showCard}
      onMouseLeave={() => setIsHovering(false)}
      onFocus={showCard}
      onBlur={() => setIsHovering(false)}
    >
      <button
        type="button"
        disabled={!canOpenProfile}
        onClick={openProfile}
        className={`max-w-full truncate text-left font-semibold underline-offset-4 transition ${
          canOpenProfile ? "cursor-pointer hover:underline" : "cursor-default"
        } ${nameClassName}`}
      >
        {fallbackName}
      </button>

      {subtitle ? (
        <div className={`truncate text-xs ${subtitleClassName}`}>
          {subtitle}
        </div>
      ) : null}

      {hoverCard ? createPortal(hoverCard, document.body) : null}
    </div>
  );
}
